import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Copy, Check, Key, Zap, AlertTriangle,
  Code2, ChevronRight, ExternalLink, Shield, Radio,
  Terminal, Hash, Globe, Search,
} from 'lucide-react';
import { ThemeToggle } from '../context/ThemeContext';
import BrandLogo from './BrandLogo';

const DOMAIN = 'opensov.com';
const API_BASE = `https://api.${DOMAIN}`;

export default function ApiDocs() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState('');
  const [activeSection, setActiveSection] = useState('quickstart');

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const sections = [
    { id: 'quickstart', label: 'Quick Start', icon: Zap },
    { id: 'authentication', label: 'Authentication', icon: Shield },
    { id: 'chat-completions', label: 'Chat Completions', icon: Code2 },
    { id: 'streaming', label: 'Streaming', icon: Radio },
    { id: 'models', label: 'Models', icon: Hash },
    { id: 'rate-limits', label: 'Rate Limits', icon: Globe },
    { id: 'errors', label: 'Error Handling', icon: AlertTriangle },
    { id: 'sdks', label: 'SDKs & Libraries', icon: Terminal },
  ];

  useEffect(() => {
    const handleScroll = () => {
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140 && rect.bottom > 140) { setActiveSection(s.id); break; }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CodeBlock = ({ code, lang = 'bash', id }) => (
    <div className="relative group my-4 rounded-xl overflow-hidden border border-line">
      <div className="flex items-center justify-between px-4 py-2.5 bg-app-alt border-b border-line">
        <span className="text-[10px] font-mono text-content-3 uppercase tracking-[0.15em]">{lang}</span>
        <button onClick={() => copyCode(code, id)}
          className="flex items-center gap-1.5 text-[10px] text-content-3 hover:text-emerald-500 transition-colors"
          data-testid={`copy-${id}`}>
          {copied === id ? <><Check className="w-3 h-3 text-emerald-500" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      <pre className="bg-surface p-5 overflow-x-auto text-[13px] leading-relaxed">
        <code className="text-content-2 font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );

  const Endpoint = ({ method, path }) => (
    <div className="p-4 bg-app-alt border border-line rounded-xl mb-6 flex items-center gap-3">
      <span className={`text-[10px] font-mono font-bold px-2.5 py-1 rounded-md uppercase tracking-wider
        ${method === 'POST' ? 'text-emerald-500 bg-emerald-500/10' : 'text-blue-500 bg-blue-500/10'}`}>
        {method}
      </span>
      <code className="text-sm text-content font-mono">{path}</code>
    </div>
  );

  const Th = ({ children }) => (
    <th className="text-left py-3 px-4 font-mono text-content-3 uppercase tracking-wider text-[10px]">{children}</th>
  );

  const ParamTable = ({ params }) => (
    <div className="overflow-x-auto mb-8 rounded-xl border border-line">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-app-alt"><Th>Parameter</Th><Th>Type</Th><Th>Required</Th><Th>Description</Th></tr>
        </thead>
        <tbody>
          {params.map(([param, type, req, desc], i) => (
            <tr key={i} className="border-t border-line">
              <td className="py-3 px-4 font-mono text-emerald-500">{param}</td>
              <td className="py-3 px-4 font-mono text-content-3">{type}</td>
              <td className="py-3 px-4"><span className={req === 'Yes' ? 'text-amber-500' : 'text-content-3'}>{req}</span></td>
              <td className="py-3 px-4 text-content-2">{desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const Step = ({ n, title, children }) => (
    <div className="flex items-start gap-4">
      <span className="w-8 h-8 flex items-center justify-center bg-emerald-500/10 text-emerald-500 text-xs font-mono font-bold flex-shrink-0 rounded-lg border border-emerald-500/20">{n}</span>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-content mb-1">{title}</h3>
        {children}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-app text-content" data-testid="api-docs">
      {/* Header */}
      <header className="border-b border-line bg-app/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-content-3 hover:text-content transition-colors" data-testid="docs-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2.5">
              <BrandLogo className="h-8" />
              <span className="font-heading font-bold text-base tracking-tight hidden sm:inline">API Reference</span>
              <span className="text-[9px] font-mono text-emerald-500 border border-emerald-500/25 bg-emerald-500/[0.08] px-2 py-0.5 rounded uppercase tracking-wider">v1</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button onClick={() => navigate('/dashboard')}
              className="hidden sm:flex items-center gap-2 px-3.5 py-2 bg-overlay text-content text-xs font-medium border border-line-2 hover:bg-overlay-hover rounded-lg transition-colors"
              data-testid="docs-dashboard-btn">
              Dashboard <ExternalLink className="w-3 h-3 opacity-50" />
            </button>
            <button onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 rounded-lg transition-colors"
              data-testid="get-api-key-btn">
              <Key className="w-3.5 h-3.5" /> Get API Key
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 flex gap-0">
        {/* Sidebar nav */}
        <nav className="hidden lg:block w-60 flex-shrink-0 py-10 pr-8 border-r border-line sticky top-[61px] h-[calc(100vh-61px)] overflow-y-auto scrollbar-thin">
          <p className="text-[9px] font-mono text-content-3 uppercase tracking-[0.2em] mb-4 font-bold">API Reference</p>
          <div className="space-y-0.5">
            {sections.map(s => (
              <button key={s.id} onClick={() => { setActiveSection(s.id); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg transition-colors
                  ${activeSection === s.id ? 'bg-emerald-500/10 text-emerald-500 font-medium' : 'text-content-2 hover:text-content hover:bg-overlay'}`}
                data-testid={`docs-nav-${s.id}`}>
                <s.icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-line">
            <a href="mailto:api@opensov.com" className="flex items-center gap-2 px-3 py-2 text-[13px] text-content-3 hover:text-content transition-colors">
              <Search className="w-3.5 h-3.5" /> Need help?
            </a>
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 py-10 lg:pl-12 max-w-4xl min-w-0">
          {/* Intro hero */}
          <div className="mb-16 pb-10 border-b border-line">
            <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-content-3 mb-4 font-semibold">Developers</p>
            <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight leading-[1.05] mb-4">
              Build with the<br />OpenSov API
            </h1>
            <p className="text-content-2 text-base md:text-lg leading-relaxed max-w-2xl">
              A fast, OpenAI-compatible API for chat completions with real-time streaming.
              Drop it into your existing stack in minutes.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => document.getElementById('quickstart')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 bg-btn text-btn-foreground px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
                Quick Start <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={() => navigate('/dashboard')}
                className="inline-flex items-center gap-2 border border-line-2 text-content px-5 py-2.5 rounded-full text-sm font-medium hover:bg-overlay transition-colors">
                <Key className="w-3.5 h-3.5" /> Get your API key
              </button>
            </div>
          </div>

          {/* Quick Start */}
          <section id="quickstart" className="mb-20 scroll-mt-24">
            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-5 h-5 text-emerald-500" />
              <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">Quick Start</h2>
            </div>
            <p className="text-content-2 text-sm mb-8 leading-relaxed">Get up and running with the OpenSov API in under 2 minutes.</p>

            <div className="space-y-8">
              <Step n="1" title="Get your API key">
                <p className="text-xs text-content-2">Create an account and generate your API key from the <a href="/dashboard" className="text-emerald-500 hover:underline">Dashboard</a>.</p>
              </Step>
              <Step n="2" title="Make your first API call">
                <CodeBlock id="quickstart-curl" lang="bash" code={`curl -X POST ${API_BASE}/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer sk-opensov-YOUR_KEY" \\
  -d '{
    "model": "opensov-1",
    "messages": [
      {"role": "user", "content": "What is sovereign AI?"}
    ]
  }'`} />
              </Step>
              <Step n="3" title="Use in your application">
                <p className="text-xs text-content-2">The OpenSov API is <strong className="text-content">OpenAI-compatible</strong> — a drop-in replacement for most libraries.</p>
              </Step>
            </div>
          </section>

          {/* Authentication */}
          <section id="authentication" className="mb-20 scroll-mt-24">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">Authentication</h2>
            </div>
            <p className="text-content-2 text-sm mb-6 leading-relaxed">All API requests require a Bearer token in the Authorization header.</p>

            <div className="p-5 bg-app-alt border border-line rounded-xl mb-4">
              <p className="text-xs text-content-3 mb-2">Request Header</p>
              <code className="text-sm text-emerald-500 font-mono break-all">Authorization: Bearer sk-opensov-YOUR_API_KEY</code>
            </div>

            <div className="p-4 bg-amber-500/[0.08] border border-amber-500/25 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-amber-600 dark:text-amber-300 font-semibold mb-1">Keep your API keys secure</p>
                <p className="text-xs text-content-2 leading-relaxed">Never expose your API key in client-side code, public repositories, or browser requests. Always make API calls from your server.</p>
              </div>
            </div>
          </section>

          {/* Chat Completions */}
          <section id="chat-completions" className="mb-20 scroll-mt-24">
            <div className="flex items-center gap-3 mb-2">
              <Code2 className="w-5 h-5 text-emerald-500" />
              <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">Chat Completions</h2>
            </div>
            <p className="text-content-2 text-sm mb-6 leading-relaxed">Generate AI responses given a conversation history.</p>

            <Endpoint method="POST" path={`${API_BASE}/v1/chat/completions`} />

            <h3 className="text-sm font-semibold text-content mb-4">Request Body</h3>
            <ParamTable params={[
              ['model', 'string', 'No', 'Model ID. Default: "opensov-1"'],
              ['messages', 'array', 'Yes', 'Array of message objects with role and content'],
              ['temperature', 'float', 'No', 'Sampling temperature (0-2). Default: 0.7'],
              ['max_tokens', 'integer', 'No', 'Maximum tokens in response. Default: 4096'],
              ['stream', 'boolean', 'No', 'Enable streaming via SSE. Default: false'],
            ]} />

            <h3 className="text-sm font-semibold text-content mb-3">Python Example</h3>
            <CodeBlock id="python-example" lang="python" code={`import requests

response = requests.post(
    "${API_BASE}/v1/chat/completions",
    headers={
        "Authorization": "Bearer sk-opensov-YOUR_KEY",
        "Content-Type": "application/json",
    },
    json={
        "model": "opensov-1",
        "messages": [
            {"role": "system", "content": "You are a helpful assistant."},
            {"role": "user", "content": "Explain quantum computing"},
        ],
        "temperature": 0.7,
    },
)

data = response.json()
print(data["choices"][0]["message"]["content"])`} />

            <h3 className="text-sm font-semibold text-content mb-3 mt-8">JavaScript Example</h3>
            <CodeBlock id="js-example" lang="javascript" code={`const response = await fetch("${API_BASE}/v1/chat/completions", {
  method: "POST",
  headers: {
    "Authorization": "Bearer sk-opensov-YOUR_KEY",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    model: "opensov-1",
    messages: [
      { role: "user", content: "What is India's AI strategy?" }
    ],
  }),
});

const data = await response.json();
console.log(data.choices[0].message.content);`} />

            <h3 className="text-sm font-semibold text-content mb-3 mt-8">OpenAI SDK Compatible</h3>
            <CodeBlock id="openai-compat" lang="python" code={`from openai import OpenAI

client = OpenAI(
    api_key="sk-opensov-YOUR_KEY",
    base_url="${API_BASE}/v1",
)

response = client.chat.completions.create(
    model="opensov-1",
    messages=[
        {"role": "user", "content": "Hello, OpenSov!"}
    ],
)

print(response.choices[0].message.content)`} />

            <h3 className="text-sm font-semibold text-content mb-3 mt-8">Response Format</h3>
            <CodeBlock id="response-format" lang="json" code={`{
  "id": "chatcmpl-abc123def456",
  "object": "chat.completion",
  "created": 1709000000,
  "model": "opensov-1",
  "choices": [
    {
      "index": 0,
      "message": {
        "role": "assistant",
        "content": "Sovereign AI refers to..."
      },
      "finish_reason": "stop"
    }
  ],
  "usage": {
    "prompt_tokens": 12,
    "completion_tokens": 150,
    "total_tokens": 162
  }
}`} />
          </section>

          {/* Streaming */}
          <section id="streaming" className="mb-20 scroll-mt-24">
            <div className="flex items-center gap-3 mb-2">
              <Radio className="w-5 h-5 text-emerald-500" />
              <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">Streaming</h2>
            </div>
            <p className="text-content-2 text-sm mb-6 leading-relaxed">
              Stream responses in real-time using Server-Sent Events (SSE). Set <code className="text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded text-xs">stream: true</code> in your request body.
            </p>

            <h3 className="text-sm font-semibold text-content mb-3">cURL with Streaming</h3>
            <CodeBlock id="streaming-curl" lang="bash" code={`curl -X POST ${API_BASE}/v1/chat/completions \\
  -H "Authorization: Bearer sk-opensov-YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "opensov-1",
    "messages": [{"role": "user", "content": "Tell me a story"}],
    "stream": true
  }'`} />

            <h3 className="text-sm font-semibold text-content mb-3 mt-8">Python Streaming</h3>
            <CodeBlock id="streaming-python" lang="python" code={`import requests

response = requests.post(
    "${API_BASE}/v1/chat/completions",
    headers={
        "Authorization": "Bearer sk-opensov-YOUR_KEY",
        "Content-Type": "application/json",
    },
    json={
        "model": "opensov-1",
        "messages": [{"role": "user", "content": "Tell me a story"}],
        "stream": True,
    },
    stream=True,
)

for line in response.iter_lines():
    if line:
        line = line.decode("utf-8")
        if line.startswith("data: ") and line != "data: [DONE]":
            import json
            chunk = json.loads(line[6:])
            delta = chunk["choices"][0]["delta"]
            if "content" in delta:
                print(delta["content"], end="", flush=True)`} />

            <h3 className="text-sm font-semibold text-content mb-3 mt-8">OpenAI SDK Streaming</h3>
            <CodeBlock id="streaming-openai" lang="python" code={`from openai import OpenAI

client = OpenAI(
    api_key="sk-opensov-YOUR_KEY",
    base_url="${API_BASE}/v1",
)

stream = client.chat.completions.create(
    model="opensov-1",
    messages=[{"role": "user", "content": "Tell me a story"}],
    stream=True,
)

for chunk in stream:
    if chunk.choices[0].delta.content:
        print(chunk.choices[0].delta.content, end="", flush=True)`} />
          </section>

          {/* Models */}
          <section id="models" className="mb-20 scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight mb-6">Models</h2>
            <Endpoint method="GET" path={`${API_BASE}/v1/models`} />
            <div className="overflow-x-auto rounded-xl border border-line">
              <table className="w-full text-xs">
                <thead><tr className="bg-app-alt"><Th>Model</Th><Th>Context</Th><Th>Best For</Th></tr></thead>
                <tbody className="text-content-2">
                  <tr className="border-t border-line">
                    <td className="py-3 px-4 font-mono text-emerald-500">opensov-1</td>
                    <td className="py-3 px-4">128K tokens</td>
                    <td className="py-3 px-4">General purpose, fast responses, reasoning</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <CodeBlock id="list-models" lang="bash" code={`curl ${API_BASE}/v1/models \\
  -H "Authorization: Bearer sk-opensov-YOUR_KEY"`} />
          </section>

          {/* Rate Limits */}
          <section id="rate-limits" className="mb-20 scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight mb-6">Rate Limits</h2>
            <div className="overflow-x-auto rounded-xl border border-line">
              <table className="w-full text-xs">
                <thead><tr className="bg-app-alt"><Th>Tier</Th><Th>Requests/Day</Th><Th>Max Keys</Th><Th>Streaming</Th></tr></thead>
                <tbody className="text-content-2">
                  <tr className="border-t border-line">
                    <td className="py-3 px-4">Free</td>
                    <td className="py-3 px-4">100</td>
                    <td className="py-3 px-4">5</td>
                    <td className="py-3 px-4 text-emerald-500">Supported</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-4 bg-app-alt border border-line rounded-xl">
              <p className="text-xs text-content-2 leading-relaxed">
                When you exceed the rate limit, the API returns a <code className="text-emerald-500 bg-emerald-500/10 px-1 py-0.5 rounded">429</code> status code.
                Implement exponential backoff in your retry logic. Rate limits reset daily at midnight UTC.
              </p>
            </div>
          </section>

          {/* Error Handling */}
          <section id="errors" className="mb-20 scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight mb-6">Error Handling</h2>
            <p className="text-content-2 text-sm mb-6 leading-relaxed">All errors follow a consistent JSON format:</p>
            <CodeBlock id="error-format" lang="json" code={`{
  "detail": {
    "error": {
      "message": "Human-readable error description",
      "type": "error_type",
      "code": "error_code"
    }
  }
}`} />
            <div className="overflow-x-auto mt-6 rounded-xl border border-line">
              <table className="w-full text-xs">
                <thead><tr className="bg-app-alt"><Th>HTTP Code</Th><Th>Error Type</Th><Th>Description</Th></tr></thead>
                <tbody className="text-content-2">
                  {[
                    ['401', 'authentication_error', 'Invalid or missing API key'],
                    ['400', 'invalid_request_error', 'Malformed request body'],
                    ['429', 'rate_limit_error', 'Too many requests — daily limit exceeded'],
                    ['500', 'server_error', 'Internal server error'],
                  ].map(([code, type, desc], i) => (
                    <tr key={i} className="border-t border-line">
                      <td className="py-3 px-4 font-mono text-amber-500">{code}</td>
                      <td className="py-3 px-4 font-mono text-content-3">{type}</td>
                      <td className="py-3 px-4">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* SDKs */}
          <section id="sdks" className="mb-20 scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight mb-4">SDKs & Libraries</h2>
            <p className="text-content-2 text-sm mb-6 leading-relaxed">
              OpenSov API is <strong className="text-content">OpenAI-compatible</strong>. Use any OpenAI SDK by changing the <code className="text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded text-xs">base_url</code>:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { lang: 'Python', lib: 'openai', install: 'pip install openai', config: `client = OpenAI(\n    api_key="sk-opensov-...",\n    base_url="${API_BASE}/v1"\n)` },
                { lang: 'JavaScript', lib: 'openai', install: 'npm install openai', config: `const client = new OpenAI({\n    apiKey: "sk-opensov-...",\n    baseURL: "${API_BASE}/v1"\n});` },
              ].map((sdk, i) => (
                <div key={i} className="p-6 bg-surface border border-line rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-content">{sdk.lang}</h3>
                    <span className="text-[9px] font-mono text-content-3 border border-line px-2 py-1 rounded">{sdk.lib}</span>
                  </div>
                  <code className="text-[11px] text-content-2 font-mono block mb-3">$ {sdk.install}</code>
                  <pre className="text-[11px] text-content-2 font-mono bg-app-alt p-3 border border-line rounded-lg overflow-x-auto">{sdk.config}</pre>
                </div>
              ))}
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-line pt-8 flex items-center justify-between">
            <p className="text-content-3 text-xs">
              Need help? <a href="mailto:api@opensov.com" className="text-emerald-500 hover:underline">api@opensov.com</a>
            </p>
            <button onClick={() => navigate('/dashboard')}
              className="text-xs text-emerald-500 hover:underline flex items-center gap-1" data-testid="docs-footer-dashboard">
              Go to Dashboard <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
